import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { D365FormRow, d365ControlSx } from '@/components/D365FormControls';

import { createClient, createClientContact } from '@/features/clients/api/clients';
import {
  resolveAddress,
  searchAddresses,
  type AddressSuggestion,
} from '@/features/addresses/api/addresses';

export function NewClientPage() {
  const navigate = useNavigate();
  const [addressQuery, setAddressQuery] = useState('');
  const [debouncedAddressQuery, setDebouncedAddressQuery] = useState('');
  const [form, setForm] = useState({
    clientName: '',
    billingAddress: '',
    contactName: '',
    email: '',
    phone: '',
    homePhone: '',
    notifyByEmail: false,
    notifyBySms: false,
  });
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedAddressQuery(addressQuery.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [addressQuery]);

  const suggestions = useQuery({
    queryKey: ['address-search', debouncedAddressQuery],
    queryFn: () => searchAddresses(debouncedAddressQuery),
    enabled: debouncedAddressQuery.length >= 3,
    staleTime: 60_000,
  });
  const selectedAddress = useMutation({
    mutationFn: (suggestion: AddressSuggestion) => resolveAddress(suggestion.udprn),
    onSuccess: (address) => {
      const formatted = [
        address.address_line_1,
        address.address_line_2,
        address.address_line_3,
        address.post_town,
        address.postcode,
      ].filter(Boolean).join('\n');
      set('billingAddress', formatted);
    },
  });
  const mutation = useMutation({
    mutationFn: async () => {
      const clientId = await createClient({
        name: form.clientName.trim(),
        billingAddress: form.billingAddress.trim(),
      });
      if (form.contactName.trim())
        await createClientContact(clientId, {
          name: form.contactName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          homePhone: form.homePhone.trim(),
          notifyByEmail: form.notifyByEmail,
          notifyBySms: form.notifyBySms,
        });
      return clientId;
    },
    onSuccess: (clientId) => void navigate(`/clients/${clientId}`),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (form.clientName.trim()) mutation.mutate();
  };

  return (
    <Box component="form" onSubmit={submit} sx={{ p: 1 }}>
      <Paper elevation={0} sx={{ ...paperSx, mb: 1 }}>
        <Stack direction="row" sx={{ minHeight: 44, px: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => void navigate('/clients')}>Back</Button>
          <Button type="submit" startIcon={<SaveOutlinedIcon />} disabled={!form.clientName.trim() || mutation.isPending}>Save</Button>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ ...paperSx, mb: 1.5 }}>
        <Stack direction="row" alignItems="center" gap={2} sx={{ px: 2.5, pt: 2 }}>
          <Avatar sx={{ bgcolor: '#d2d0ce', color: '#323130' }}>NC</Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h1">New Client</Typography>
            <Typography variant="body2">Client</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography color="primary" fontWeight={600}>PoolAppy Manager</Typography>
            <Typography variant="caption">Owner</Typography>
          </Box>
        </Stack>
        <Tabs value={0} variant="scrollable" sx={{ mt: 1.5, px: 2.5 }}>
          <Tab label="Summary" />
          <Tab label="Contacts" disabled />
          <Tab label="Service Locations" disabled />
          <Tab label="Notes" disabled />
        </Tabs>
      </Paper>

      {mutation.isError && <Alert severity="error" sx={{ mb: 1.5 }}>The client could not be created. Check the details and try again.</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(520px, 1fr) minmax(520px, 1fr)' }, gap: 1.5 }}>
        <Paper sx={sectionSx} elevation={0}>
          <Typography variant="subtitle2" sx={sectionTitleSx}>CLIENT INFORMATION</Typography>
          <Stack spacing={1}>
            <D365FormRow label="Client name" required>
              <TextField autoFocus required value={form.clientName} onChange={(e) => set('clientName', e.target.value)} placeholder="---" sx={d365ControlSx} />
            </D365FormRow>
            <D365FormRow label="Find billing address">
              <Autocomplete<AddressSuggestion, false, false, false>
                options={suggestions.data ?? []}
                filterOptions={(options) => options}
                getOptionLabel={(option) => option.suggestion}
                isOptionEqualToValue={(option, value) => option.udprn === value.udprn}
                loading={suggestions.isFetching || selectedAddress.isPending}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input' || reason === 'clear') setAddressQuery(value);
                }}
                onChange={(_event, value) => { if (value) selectedAddress.mutate(value); }}
                noOptionsText={addressQuery.trim().length < 3 ? 'Enter at least 3 characters' : 'No addresses found'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Start typing an address or postcode"
                    sx={d365ControlSx}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: <>{(suggestions.isFetching || selectedAddress.isPending) && <CircularProgress color="inherit" size={18} />}{params.InputProps.endAdornment}</>,
                      },
                    }}
                  />
                )}
              />
            </D365FormRow>
            {(suggestions.isError || selectedAddress.isError) && <Alert severity="error">Address lookup is unavailable. Enter the billing address manually.</Alert>}
            <D365FormRow label="Billing address" align="start">
              <TextField multiline minRows={3} value={form.billingAddress} onChange={(e) => set('billingAddress', e.target.value)} placeholder="---" sx={d365ControlSx} />
            </D365FormRow>
          </Stack>
        </Paper>

        <Stack spacing={1.5}>
          <Paper sx={sectionSx} elevation={0}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>PRIMARY CONTACT</Typography>
            <Stack spacing={1}>
              <D365FormRow label="Contact name"><TextField value={form.contactName} onChange={(e) => set('contactName', e.target.value)} placeholder="---" sx={d365ControlSx} /></D365FormRow>
              <D365FormRow label="Email"><TextField type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="---" sx={d365ControlSx} /></D365FormRow>
              <D365FormRow label="Mobile"><TextField value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="---" sx={d365ControlSx} /></D365FormRow>
              <D365FormRow label="Other"><TextField value={form.homePhone} onChange={(e) => set('homePhone', e.target.value)} placeholder="---" sx={d365ControlSx} /></D365FormRow>
              <D365FormRow label="Notify by email"><Checkbox checked={form.notifyByEmail} onChange={(e) => set('notifyByEmail', e.target.checked)} /></D365FormRow>
              <D365FormRow label="Notify by SMS"><Checkbox checked={form.notifyBySms} onChange={(e) => set('notifyBySms', e.target.checked)} /></D365FormRow>
            </Stack>
          </Paper>
          <Paper sx={{ ...sectionSx, minHeight: 160 }} elevation={0}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>TIMELINE</Typography>
            <Typography variant="h2" align="center" sx={{ mt: 3 }}>Almost there</Typography>
            <Typography color="text.secondary" align="center" sx={{ mt: 1 }}>Select Save to create the record and start its timeline.</Typography>
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
}

const paperSx = { border: '1px solid', borderColor: 'divider', borderRadius: 1.5, boxShadow: '0 2px 6px rgba(0,0,0,.14)' };
const sectionSx = { p: { xs: 1.5, sm: 2 }, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, boxShadow: '0 2px 6px rgba(0,0,0,.12)' };
const sectionTitleSx = { mb: 2, fontSize: { xs: '1rem', sm: '0.875rem' }, fontWeight: 600 };

